// ═══════════════════════════════════════════════════════════════════════════
// @dxf-ai-editor/command-registry — layout category descriptors
// ═══════════════════════════════════════════════════════════════════════════
//
// Category: "layout" — paper-space layouts and viewport authoring.
//
//   • MVIEW   — create rectangular viewports inside a paper layout.
//               Dispatches `geometry/create-viewport` (editor.ts:1013).
//               status: "implemented" with full draftSteps + parameters.
//   • MSPACE  — switch the active layout into model context (focus a
//               viewport for in-place model editing). Dispatched as
//               `shell/activate-space` with kind="viewport" (in
//               COVERAGE_ALLOWLIST). No dedicated literal yet — stub.
//   • PSPACE  — inverse of MSPACE: defocus the viewport and return to
//               paper context. Same shell dispatch + stub status.
//   • LAYOUT  — manage layout tabs. The SET (activate-by-name), RENAME,
//               NEW and DELETE sub-commands are wired: SET dispatches the
//               canonical `shell/activate-space` (editorStore.ts
//               routeLayoutCommand — same command the layout tab strip
//               uses), RENAME dispatches `shell/rename-space` (App.tsx
//               LAYOUT R routing), NEW dispatches `shell/add-space` and
//               DELETE dispatches `shell/remove-space` (LAYOUT-COMMAND-
//               HANDLERS — routeLayoutCommand + spaceMutationGuards.ts;
//               the geometry-document arm lands in
//               geometryReducerLayout.ts). Bare LAYOUT lists spaces;
//               `LAYOUT LAYOUT-N` activates by id. status: "implemented" —
//               the descriptor enumerates the four shell literals it owns.
//               The remaining TEMPLATE/SAVEAS sub-commands are tracked as
//               gap specs (docs/test-gaps/cmd-layout.md) for later phases.
//
// Sources of truth: editor.ts:1013 (geometry/create-viewport),
// draftingHelpers.ts:442-444, :559 (MVIEW alias + initial step),
// messages.ts:168-170, :2695-2697 (prompt.viewport.first/second),
// App.tsx:1227-1284 (LAYOUT R routing), App.tsx:160 (recognised list),
// spaceMutationGuards.ts (LAYOUT NEW/DELETE parsing + shared guards).
//
// COVERAGE_ALLOWLIST coordination: the LAYOUT descriptor now OWNS
// `shell/activate-space` (LAYOUT SET), `shell/rename-space` (LAYOUT
// RENAME), `shell/add-space` (LAYOUT NEW) and `shell/remove-space`
// (LAYOUT DELETE) in its commandTypeLiterals — none of them appear in
// COVERAGE_ALLOWLIST (per the allowlist editing rule:
// "the list shrinks as descriptor coverage grows"). `shell/set-layout-context`
// stays in the allowlist as pure session/shell plumbing (no AutoCAD
// command-name analogue — the user never types it; it is dispatched by the
// paper/viewport context toggle and by LAYOUT SET's already-current restore).
// MSPACE / PSPACE remain typed-name wrappers with empty commandTypeLiterals
// (they inline-mutate in editorStore today); when dedicated
// `space/activate-paper` / `space/activate-model` literals land those
// descriptors should flip to status: "implemented" and enumerate them.
// ═══════════════════════════════════════════════════════════════════════════

import type { CommandDescriptor } from "../types.js";

const REDUCER_GEOMETRY = "apps/web/src/app/state/geometryReducer.ts" as const;
// Per-viewport override commands (VPCLIP set/remove) route through
// `applyLayoutAction` → `computeViewportEditMutation` here (the shared
// mutated-vs-no-op truth), not the generic geometry reducer.
const REDUCER_VIEWPORT =
  "apps/web/src/app/state/geometryReducerLayout.ts" as const;

const MVIEW: CommandDescriptor = {
  id: "MVIEW",
  category: "layout",
  status: "implemented",
  aiCallable: true,
  aliases: {
    en: ["MVIEW", "MV", "VIEWPORT", "-VPORTS"],
    ru: ["МВИДЭКР", "МВИД", "МВ"],
  },
  commandTypeLiterals: ["geometry/create-viewport"],
  draftCommandId: "MVIEW",
  draftSteps: [
    {
      stepId: "viewport-first-corner",
      promptKeyRu: "prompt.viewport.first",
      promptKeyEn: "prompt.viewport.first",
      expects: "point",
      keywords: ["FIT", "F"],
    },
    {
      stepId: "viewport-second-corner",
      promptKeyRu: "prompt.viewport.second",
      promptKeyEn: "prompt.viewport.second",
      expects: "point",
    },
  ],
  parameters: [
    {
      name: "spaceId",
      type: "string",
      required: true,
      description: {
        ru: "Идентификатор пространства листа (paper layout), в котором создаётся видовой экран.",
        en: "Paper layout space identifier where the viewport is created.",
      },
    },
    {
      name: "corner1",
      type: "point2d",
      required: true,
      description: {
        ru: "Первый угол прямоугольника видового экрана в координатах листа (x, y).",
        en: "First corner of the viewport rectangle in paper coordinates (x, y).",
      },
    },
    {
      name: "corner2",
      type: "point2d",
      required: true,
      description: {
        ru: "Противоположный угол прямоугольника видового экрана в координатах листа (x, y).",
        en: "Opposite corner of the viewport rectangle in paper coordinates (x, y).",
      },
    },
    {
      name: "modelExtent",
      type: "object",
      required: false,
      description: {
        ru: "Опциональные границы модели (minX, minY, maxX, maxY) для подгонки видового экрана к содержимому модели (MVIEW FIT). При отсутствии используется viewHeight=100, центр (0,0).",
        en: "Optional model extent (minX, minY, maxX, maxY) used to auto-fit the viewport to model contents (MVIEW FIT). When absent the viewport falls back to viewHeight=100, center=(0,0).",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Создаёт прямоугольный видовой экран на листе для просмотра модели в paper space.",
      long: "Команда МВИДЭКР (MVIEW) создаёт сущность VIEWPORT (видовой экран) внутри активного пространства листа. Видовой экран — это окно в модельное пространство: он рендерит содержимое model space с заданным масштабом и точкой обзора. Запрашиваются два противоположных угла прямоугольника в координатах листа. На первом шаге можно ввести FIT (Подгон) для автоматической подгонки видового экрана к границам модели — система использует переданный modelExtent или fallback viewHeight=100 c центром в (0,0). Команда работает только в пространстве листа (LAYOUT), в model space она недоступна. Видовой экран можно затем редактировать для изменения масштаба, центра обзора и блокировки.",
      examples: ["МВИДЭКР 0,0 200,150", "MVIEW FIT", "MV 10,10 @180,140"],
      seeAlso: ["LAYOUT", "MSPACE", "PSPACE"],
    },
    en: {
      short:
        "Creates a rectangular viewport on a paper-space layout for viewing model space.",
      long: "The MVIEW command creates a VIEWPORT entity inside the active paper-space layout. A viewport is a window onto model space: it renders the model contents at a chosen scale and view center. Prompts for the two opposite corners of a rectangle in paper-space coordinates. On the first step you can type FIT to auto-fit the viewport to the model bounds — the system uses the supplied modelExtent or falls back to viewHeight=100 centered at (0,0). The command only operates inside a layout (paper) space; it is unavailable in model space. After creation the viewport can be edited to change scale, view center and lock state.",
      examples: ["MVIEW 0,0 200,150", "MVIEW FIT", "MV 10,10 @180,140"],
      seeAlso: ["LAYOUT", "MSPACE", "PSPACE"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.viewport.label",
    labelKeyEn: "command.viewport.label",
    ribbonTab: "layout",
    ribbonSection: "ribbon.section.viewport_context",
    glyph: "VP",
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/create-viewport", file: REDUCER_GEOMETRY },
  ],
};

const MSPACE: CommandDescriptor = {
  id: "MSPACE",
  category: "layout",
  status: "stub",
  aiCallable: true,
  aliases: { en: ["MSPACE", "MS"], ru: ["МПРОСТР", "ПРОСТРМ"] },
  commandTypeLiterals: [],
  parameters: [],
  help: {
    ru: {
      short:
        "Переключает активный лист в режим работы внутри видового экрана (model context).",
      long: 'Команда МПРОСТР (MSPACE) на активном листе пространства бумаги переводит фокус ввода внутрь видового экрана — все последующие команды редактирования будут работать с сущностями модельного пространства, отображаемыми через этот видовой экран. Курсор переключается на координатную систему модели; границы видового экрана подсвечиваются. Команда работает только когда активна вкладка листа (LAYOUT) и на ней есть хотя бы один видовой экран. В model space команда игнорируется (используйте PSPACE на листе). Внутренне диспатчится через shell/activate-space с kind="viewport".',
      examples: ["МПРОСТР", "MSPACE"],
      seeAlso: ["PSPACE", "MVIEW", "LAYOUT"],
    },
    en: {
      short:
        "Switches the active layout into model context (focus a viewport for in-place model editing).",
      long: 'The MSPACE command on the active paper-space layout shifts input focus INTO a viewport — all subsequent editing commands operate on the model-space entities seen through that viewport. The cursor adopts model-space coordinates; the active viewport border is highlighted. The command requires that a layout tab is active and contains at least one viewport. In model space it is ignored (use PSPACE from a layout). Internally dispatched as shell/activate-space with kind="viewport".',
      examples: ["MSPACE", "MS"],
      seeAlso: ["PSPACE", "MVIEW", "LAYOUT"],
    },
  },
  uiMetadata: {
    labelKeyRu: "ribbon.action.viewport",
    labelKeyEn: "ribbon.action.viewport",
    ribbonTab: "layout",
    ribbonSection: "ribbon.section.viewport_context",
    glyph: "MS",
  },
  reducerLocations: [],
};

const PSPACE: CommandDescriptor = {
  id: "PSPACE",
  category: "layout",
  status: "stub",
  aiCallable: true,
  aliases: { en: ["PSPACE", "PS"], ru: ["ПЛИСТ", "ЛИСТП"] },
  commandTypeLiterals: [],
  parameters: [],
  help: {
    ru: {
      short:
        "Возвращает активный лист в режим работы по бумажному пространству (paper context).",
      long: 'Команда ПЛИСТ (PSPACE) на активной вкладке листа возвращает фокус ввода из видового экрана обратно в координаты листа. Курсор переключается с координат модели на координаты бумаги; границы ранее активного видового экрана перестают подсвечиваться. Все последующие команды работают с сущностями paper space (рамки, надписи, аннотации в координатах листа). PSPACE — обратная операция к МПРОСТР; обе доступны только когда активна вкладка LAYOUT. В model space команда игнорируется. Внутренне диспатчится через shell/activate-space с kind="paper".',
      examples: ["ПЛИСТ", "PSPACE"],
      seeAlso: ["MSPACE", "MVIEW", "LAYOUT"],
    },
    en: {
      short:
        "Returns the active layout to paper context (defocus the viewport).",
      long: 'The PSPACE command on the active layout tab shifts input focus from inside a viewport BACK to paper-space coordinates. The cursor changes from model-space to paper coordinates; the previously-active viewport border stops being highlighted. All subsequent commands operate on paper-space entities (title block, annotations, paper-coordinate text). PSPACE is the inverse of MSPACE; both are only available when a LAYOUT tab is active. In model space it is ignored. Internally dispatched as shell/activate-space with kind="paper".',
      examples: ["PSPACE", "PS"],
      seeAlso: ["MSPACE", "MVIEW", "LAYOUT"],
    },
  },
  uiMetadata: {
    labelKeyRu: "ribbon.action.paper",
    labelKeyEn: "ribbon.action.paper",
    ribbonTab: "layout",
    ribbonSection: "ribbon.section.viewport_context",
    glyph: "PS",
  },
  reducerLocations: [],
};

const LAYOUT: CommandDescriptor = {
  id: "LAYOUT",
  category: "layout",
  status: "implemented",
  aiCallable: true,
  aliases: { en: ["LAYOUT", "-LAYOUT"], ru: ["МАКЕТ", "РАЗМЛИСТ"] },
  commandTypeLiterals: [
    "shell/activate-space",
    "shell/rename-space",
    "shell/add-space",
    "shell/remove-space",
  ],
  parameters: [
    {
      name: "subCommand",
      type: "enum",
      enumValues: ["RENAME", "NEW", "DELETE", "SET", "TEMPLATE", "SAVEAS", "?"],
      required: true,
      description: {
        ru: "Подкоманда: SET (сделать текущим), RENAME (переименовать), NEW (создать), DELETE (удалить), TEMPLATE (из шаблона), SAVEAS (сохранить), ? (список). Реализованы SET, RENAME, NEW и DELETE; TEMPLATE/SAVEAS запланированы.",
        en: "Sub-command: SET (make current), RENAME, NEW, DELETE, TEMPLATE, SAVEAS, ? (list). SET, RENAME, NEW and DELETE are wired; TEMPLATE/SAVEAS are scheduled.",
      },
    },
    {
      name: "oldName",
      type: "string",
      required: false,
      description: {
        ru: "Имя листа (для SET / RENAME / DELETE). Только листы пространства бумаги; поиск регистронезависимый. Для DELETE без имени удаляется текущий лист (как значение по умолчанию в AutoCAD).",
        en: "Layout name (for SET / RENAME / DELETE). Paper-space layouts only; lookup is case-insensitive. DELETE without a name targets the current layout (AutoCAD's prompt default).",
      },
    },
    {
      name: "newName",
      type: "string",
      required: false,
      description: {
        ru: "Новое имя листа (для RENAME / NEW). Имя должно быть уникальным (без учёта регистра); пустые значения и дубликаты отклоняются. Для NEW без имени используется «Layout N» (следующий свободный номер по конвенции существующих вкладок).",
        en: "New layout name (for RENAME / NEW). Must be unique (case-insensitive); empty values and duplicates are rejected. NEW without a name defaults to 'Layout N' (next free number, mirroring the existing tab convention).",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Управляет вкладками листов (активация, переименование, создание, удаление).",
      long: "Команда МАКЕТ (LAYOUT) — мульти-функциональная команда управления листами пространства бумаги. Реализованные сценарии: SET — сделать лист текущим по имени через `LAYOUT SET <имя>` или `LAYOUT S <имя>` (диспатчится через канонический shell/activate-space — тот же путь, что и у вкладок листов; устанавливает activeSpaceId и layoutContext='paper'); RENAME — переименование через `LAYOUT R <old> <new>` или `LAYOUT RENAME <old> <new>` (через shell/rename-space); NEW — создание нового ПУСТОГО листа через `LAYOUT N [имя]` / `МАКЕТ Н [имя]` (через shell/add-space; без имени используется «Layout N» со следующим свободным номером; новый лист НЕ активируется — как в AutoCAD); DELETE — удаление листа через `LAYOUT D [имя]` / `МАКЕТ УД [имя]` (через shell/remove-space; без имени удаляется текущий лист — значение по умолчанию AutoCAD; вкладку Model и последний оставшийся лист удалить нельзя; при удалении активного листа активируется соседний). SET/DELETE работают только с листами пространства бумаги (вкладку Model выбрать/удалить нельзя — это соответствует AutoCAD); несуществующее имя — безопасный no-op с предупреждением; установка уже текущего листа — no-op. Поиск имени регистронезависимый. Системный лист `Model` нельзя переименовать; дубликаты и пустые имена отклоняются (уникальность без учёта регистра); максимум 255 листов. NEW/DELETE невертируемы (undo для операций с листами — отложенная подсистема LAYOUT-MANAGE-UI-HIST). Подкоманды TEMPLATE, SAVEAS, ? запланированы на следующие фазы.",
      examples: [
        "LAYOUT SET Лист1",
        "МАКЕТ S Title_Sheet",
        "LAYOUT R Лист1 Главный_лист",
        "РАЗМЛИСТ RENAME Layout1 A1_Drawing",
        "МАКЕТ Н Новый_лист",
        "МАКЕТ УД Лист3",
      ],
      seeAlso: ["MVIEW", "MSPACE", "PSPACE"],
    },
    en: {
      short:
        "Manages paper-space layout tabs (set current, rename, create, delete).",
      long: "The LAYOUT command is the multi-purpose layout-tab manager. Implemented today: SET — make a layout current by name via `LAYOUT SET <name>` or `LAYOUT S <name>` (dispatched through the canonical shell/activate-space command — the same path the layout tab strip uses; sets activeSpaceId and layoutContext='paper'); RENAME — `LAYOUT R <old> <new>` or `LAYOUT RENAME <old> <new>` (dispatched as shell/rename-space); NEW — create a new EMPTY paper-space layout via `LAYOUT N [name]` (dispatched as shell/add-space; without a name the default is 'Layout N' with the next free number; the new tab is NOT activated — matching AutoCAD); DELETE — remove a layout via `LAYOUT D [name]` (dispatched as shell/remove-space; without a name the CURRENT layout is targeted — AutoCAD's prompt default; the Model tab and the last remaining layout cannot be deleted; deleting the active layout activates the adjacent remaining tab). SET/DELETE operate on paper-space layouts only (the Model tab is not selectable/deletable via LAYOUT — matching AutoCAD); an unknown name is a fail-closed no-op with a warning; setting the already-current layout is a no-op. Name lookup is case-insensitive. The system Model space cannot be renamed; duplicate and empty names are rejected (case-insensitive uniqueness); at most 255 layouts. NEW/DELETE ship non-invertible (space-lifecycle undo is the deferred LAYOUT-MANAGE-UI-HIST subsystem). The TEMPLATE, SAVEAS, ? sub-commands are scheduled for later phases.",
      examples: [
        "LAYOUT SET Layout1",
        "LAYOUT S Title_Sheet",
        "LAYOUT R Layout1 A1_Drawing",
        "-LAYOUT RENAME Layout1 Main",
        "LAYOUT N Sheet2",
        "LAYOUT D Sheet2",
      ],
      seeAlso: ["MVIEW", "MSPACE", "PSPACE"],
    },
  },
  uiMetadata: {
    // status:"implemented" → the validator's H.I18N check (which collects only
    // `command.<id>.<field>` keys) now applies. Use the `command.layout.label`
    // convention (RU+EN added to messages.ts) instead of the previous
    // `ribbon.action.layout_1` tab key, which H.I18N could not resolve (#290-15).
    // The LIVE ribbon layout-tab button reads `t("ribbon.action.layout_1")`
    // DIRECTLY (RibbonConfig.ts — UI-only space-routing buttons bypass the
    // registry), so this descriptor-label change leaves the visible ribbon label
    // unchanged.
    labelKeyRu: "command.layout.label",
    labelKeyEn: "command.layout.label",
    ribbonTab: "layout",
    ribbonSection: "ribbon.section.viewport_context",
    glyph: "L",
  },
  reducerLocations: [
    {
      commandTypeLiteral: "shell/activate-space",
      file: "apps/web/src/app/state/shellUiReducer.ts",
    },
    {
      commandTypeLiteral: "shell/rename-space",
      file: "apps/web/src/app/state/shellUiReducer.ts",
    },
    {
      commandTypeLiteral: "shell/add-space",
      file: "apps/web/src/app/state/shellUiReducer.ts",
    },
    {
      commandTypeLiteral: "shell/remove-space",
      file: "apps/web/src/app/state/shellUiReducer.ts",
    },
  ],
};

const VPCLIP: CommandDescriptor = {
  id: "VPCLIP",
  category: "layout",
  status: "implemented",
  // AI-callable: VPCLIP is a legitimate layout-authoring command the AI may
  // propose (like MVIEW). The AI-action schema/translator is the separate CC-2c
  // slice ("cmd-vpclip … за их дескрипторами") — this descriptor UNBLOCKS it;
  // no `aiSchemaRef` yet (mirrors MVIEW, which is aiCallable without one).
  aiCallable: true,
  aliases: { en: ["VPCLIP", "-VPCLIP"], ru: ["ВЭКРЕЗ"] },
  // VPCLIP owns the three viewport-clip authoring literals: the polygonal
  // producer (new), the rectangular reshape, and the clip delete.
  commandTypeLiterals: [
    "geometry/viewport-clip-set-polygon",
    "geometry/viewport-clip-set",
    "geometry/viewport-clip-remove",
  ],
  // VPCLIP-INTERACTIVE (MASTERPLAN W2.6) — bare `VPCLIP`/`ВЭКРЕЗ` (no
  // arguments) now opens the interactive rectangular pick session: select
  // viewport → first corner → opposite corner → `geometry/viewport-clip-set`.
  // Mirrors MVIEW's `draftCommandId` wiring above. Polygonal interactive
  // pick + a Delete-boundary affordance stay CLI-scripted-only
  // (`VPCLIP <id> P ...` / `VPCLIP <id> D`) until EPIC-VIEWPORT-CLIP's
  // persisted clip-boundary producer slice (N7 VPCLIP-BOUNDARY-EXPORT).
  draftCommandId: "VPCLIP",
  draftSteps: [
    {
      stepId: "vpclip-select-viewport",
      promptKeyRu: "prompt.vpclip.select_viewport",
      promptKeyEn: "prompt.vpclip.select_viewport",
      expects: "selection",
    },
    {
      stepId: "vpclip-first-corner",
      promptKeyRu: "prompt.vpclip.first_corner",
      promptKeyEn: "prompt.vpclip.first_corner",
      expects: "point",
    },
    {
      stepId: "vpclip-second-corner",
      promptKeyRu: "prompt.vpclip.second_corner",
      promptKeyEn: "prompt.vpclip.second_corner",
      expects: "point",
    },
  ],
  parameters: [
    {
      name: "viewportId",
      type: "string",
      required: true,
      description: {
        ru: "Идентификатор (handle) видового экрана, границу подрезки которого нужно задать или снять.",
        en: "Identifier (handle) of the viewport whose clip boundary is set or removed.",
      },
    },
    {
      name: "option",
      type: "enum",
      enumValues: ["POLYGONAL", "DELETE", "OBJECT"],
      required: false,
      description: {
        ru: "Подкоманда: POLYGONAL (многоугольная граница по точкам), DELETE (снять подрезку), OBJECT (граница из выбранного замкнутого объекта). Без ключа: 2 точки — прямоугольная граница, ≥3 точек — многоугольная.",
        en: "Sub-command: POLYGONAL (polygon boundary from points), DELETE (remove clip), OBJECT (boundary from a selected closed object). With no keyword: 2 points → rectangular boundary, ≥3 points → polygonal.",
      },
    },
    {
      name: "points",
      type: "point2d",
      required: false,
      description: {
        ru: "Точки границы подрезки в координатах листа (x,y). Для многоугольной границы — 3 и более точек; для прямоугольной — 2 противоположных угла.",
        en: "Clip-boundary points in paper-space coordinates (x,y). Polygonal: 3+ points; rectangular: 2 opposite corners.",
      },
    },
    {
      name: "objectId",
      type: "string",
      required: false,
      description: {
        ru: "Идентификатор замкнутого объекта-границы (замкнутая полилиния) для варианта OBJECT.",
        en: "Identifier of the closed boundary object (closed polyline) for the OBJECT variant.",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Задаёт или снимает границу подрезки видового экрана (многоугольную, прямоугольную или по объекту).",
      long: "Команда ВЭКРЕЗ (VPCLIP) подрезает видовой экран пространства листа непрямоугольной (многоугольной) или прямоугольной границей — модель видна только внутри границы. Скриптовые формы: `ВЭКРЕЗ <id> P x,y x,y x,y …` — многоугольная граница по точкам листа (3 и более); `ВЭКРЕЗ <id> D` — снять подрезку (вернуть прямоугольник); `ВЭКРЕЗ <id> O <objId>` — граница из выбранного замкнутого объекта (замкнутая полилиния, дуговые сегменты тесселируются); `ВЭКРЕЗ <id> x,y x,y` — прямоугольная граница по двум углам. Граница хранится как типизированный контур (полилиния точек в координатах листа + производная проекция в модель), а не булев флаг. Подрезка применяется в рендере через stencil-проход. Управляющий видовой экран (id=1, само пространство листа) не подрезается. Интерактивный выбор точек/объекта — отдельный слайс VPCLIP-INTERACTIVE.",
      examples: [
        "ВЭКРЕЗ 2A P 0,0 100,0 100,80 0,80",
        "ВЭКРЕЗ 2A D",
        "ВЭКРЕЗ 2A O 3F",
        "VPCLIP 2A 0,0 200,150",
      ],
      seeAlso: ["MVIEW", "LAYOUT", "MSPACE"],
    },
    en: {
      short:
        "Sets or removes a viewport clip boundary (polygonal, rectangular, or from an object).",
      long: "The VPCLIP command clips a paper-space viewport to a non-rectangular (polygonal) or rectangular boundary — the model shows only inside the boundary. Scripted forms: `VPCLIP <id> P x,y x,y x,y …` — polygonal boundary from paper-space points (3 or more); `VPCLIP <id> D` — delete the clip (revert to the rectangle); `VPCLIP <id> O <objId>` — boundary from a selected closed object (a closed polyline; arc segments are tessellated); `VPCLIP <id> x,y x,y` — rectangular boundary from two corners. The boundary is stored as a TYPED contour (a polyline of paper-space points plus its derived model-space projection), not a boolean flag. The clip is applied at render time via a stencil pass. The control viewport (id=1, paper space itself) is never clipped. The interactive point/object pick is the separate VPCLIP-INTERACTIVE slice.",
      examples: [
        "VPCLIP 2A P 0,0 100,0 100,80 0,80",
        "VPCLIP 2A D",
        "VPCLIP 2A O 3F",
        "VPCLIP 2A 0,0 200,150",
      ],
      seeAlso: ["MVIEW", "LAYOUT", "MSPACE"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.vpclip.label",
    labelKeyEn: "command.vpclip.label",
    ribbonTab: "layout",
    ribbonSection: "ribbon.section.viewport_context",
    glyph: "VC",
  },
  reducerLocations: [
    {
      commandTypeLiteral: "geometry/viewport-clip-set-polygon",
      file: REDUCER_VIEWPORT,
    },
    { commandTypeLiteral: "geometry/viewport-clip-set", file: REDUCER_VIEWPORT },
    {
      commandTypeLiteral: "geometry/viewport-clip-remove",
      file: REDUCER_VIEWPORT,
    },
  ],
};

export const LAYOUT_DESCRIPTORS: readonly CommandDescriptor[] = [
  LAYOUT,
  MSPACE,
  MVIEW,
  PSPACE,
  VPCLIP,
];
